import { useTranslation } from 'react-i18next';
import { Box, ChevronLeftIcon, ChevronRightIcon, HStack, IconButton, Pressable } from 'native-base';
import moment from 'moment';

import { Text } from '@pimm/base';
import { snapToFiveMinutes } from '../_helper';
import { DateTimeRangePicker } from './date-time-range-picker';

export type DateTimeRangeBarProps = {
  startDate: Date;
  endDate: Date;
  today: Date;
  onChange: (next: { startDate: Date; endDate: Date }) => void;
  // Forwarded to DateTimeRangePicker so the popover can position itself
  // inside the chart panel via `position: fixed` + bounding rect from
  // this ref. See equipment-chart-data.tsx for the rationale.
  panelRef?: React.MutableRefObject<any>;
};

type PresetValue = 0 | -3 | -7 | -30;

// Standalone outlined pill — visually distinct from the @pimm/base
// ButtonGroup used for sub-module navigation tabs (which is a connected
// gray-track segmented control). These are independent rounded pills with
// a white default and primary.500 yellow active state, sized for touch.
export const QuickPickPill = ({
  label,
  active,
  showDot,
  onPress,
}: {
  label: string;
  active: boolean;
  // True when this preset's day-count matches the current range's size
  // BUT the active range isn't exactly the preset itself (i.e. the user
  // stepped off-anchor via the back/forward chevrons). The dot signals
  // "this is the step granularity the chevrons are using" without
  // claiming the preset's own range is on the chart. Mutually exclusive
  // with `active` — `active` already implies the granularity matches.
  showDot?: boolean;
  onPress?: () => void;
}) => {
  const interactive = !!onPress;
  return (
    <Pressable onPress={onPress} disabled={!interactive} accessibilityRole="button">
      {({ isHovered }: { isHovered: boolean }) => {
        const hoverShift = interactive && isHovered;
        const bg = active ? (hoverShift ? 'primary.600' : 'primary.500') : hoverShift ? 'gray.100' : 'white';
        const borderColor = active ? (hoverShift ? 'primary.600' : 'primary.500') : hoverShift ? 'gray.400' : 'gray.300';
        return (
          <HStack
            space={1}
            h="36px"
            pl={showDot ? '8px' : '12px'}
            pr="12px"
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
            borderWidth={1}
            borderColor={borderColor}
            bg={bg}
          >
            {showDot && !active && <Box w="6px" h="6px" borderRadius="full" bg="primary.500" />}
            <Text size="md" color={active ? 'black' : 'gray.700'} fontWeight={active ? 600 : 500}>
              {label}
            </Text>
          </HStack>
        );
      }}
    </Pressable>
  );
};

// Square chevron button styled to match QuickPickPill (same 36px height,
// gray.300 border, gray.100 hover). Used for step-back / step-forward.
const StepChevron = ({ direction, disabled, onPress }: { direction: 'back' | 'forward'; disabled?: boolean; onPress?: () => void }) => {
  const interactive = !disabled && !!onPress;
  return (
    <Pressable onPress={onPress} disabled={!interactive} accessibilityRole="button" opacity={disabled ? 0.4 : 1}>
      {({ isHovered }: { isHovered: boolean }) => {
        const hoverShift = interactive && isHovered;
        const bg = hoverShift ? 'gray.100' : 'white';
        const borderColor = hoverShift ? 'gray.400' : 'gray.300';
        return (
          <HStack
            h="36px"
            w="36px"
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
            borderWidth={1}
            borderColor={borderColor}
            bg={bg}
          >
            {direction === 'back' ? <ChevronLeftIcon size="18px" color="gray.700" /> : <ChevronRightIcon size="18px" color="gray.700" />}
          </HStack>
        );
      }}
    </Pressable>
  );
};

export const DateTimeRangeBar = ({ startDate, endDate, today, onChange, panelRef }: DateTimeRangeBarProps) => {
  const { t } = useTranslation();

  const handleQuickPick = (days: PresetValue) => {
    const newStart = moment(today).add(days, 'day').startOf('day').toDate();
    const newEnd = new Date(snapToFiveMinutes(moment(today).startOf('minute').valueOf()));
    onChange({ startDate: newStart, endDate: newEnd });
  };

  // Step-back / step-forward shift the active window by its own size (in
  // whole days). Day-count uses floor of elapsed days so rolling presets
  // like "3 Days" step by exactly 3.
  const shiftDays = Math.max(1, moment(endDate).diff(moment(startDate).startOf('day'), 'days'));

  // Forward chevron is only enabled when the range ends before "now".
  const canStepForward = moment(endDate).isBefore(today, 'minute');

  const handleStep = (delta: number) => {
    const newStart = moment(startDate).add(delta, 'days').startOf('day').toDate();
    const candidateEnd = moment(endDate).add(delta, 'days');
    const newEnd = candidateEnd.isSame(today, 'day')
      ? new Date(snapToFiveMinutes(moment(today).startOf('minute').valueOf()))
      : candidateEnd.hour(23).minute(55).second(0).millisecond(0).toDate();
    onChange({ startDate: newStart, endDate: newEnd });
  };

  // Detect which preset (if any) currently matches the active range.
  const currentQuickPick = (() => {
    const endIsToday = moment(endDate).isSame(today, 'day');
    if (!endIsToday) return 'custom' as const;
    const isMidnight = moment(startDate).isSame(moment(startDate).startOf('day'));
    if (!isMidnight) return 'custom' as const;
    const daysBack = moment(today).startOf('day').diff(moment(startDate), 'days');
    if (daysBack === 0) return 0 as const;
    if (daysBack === 3) return -3 as const;
    if (daysBack === 7) return -7 as const;
    if (daysBack === 30) return -30 as const;
    return 'custom' as const;
  })();

  const isCustom = currentQuickPick === 'custom';

  // Step-granularity preset — which preset's day-count matches the window size.
  const stepGranularity: PresetValue | null = (() => {
    if (shiftDays === 1) return 0;
    if (shiftDays === 3) return -3;
    if (shiftDays === 7) return -7;
    if (shiftDays === 30) return -30;
    return null;
  })();

  const presets: { value: PresetValue; labelKey: string }[] = [
    { value: 0, labelKey: 'store_equipment:today' },
    { value: -3, labelKey: 'store_equipment:three_days' },
    { value: -7, labelKey: 'store_equipment:seven_days' },
    { value: -30, labelKey: 'store_equipment:thirty_days' },
  ];

  return (
    <HStack space={2} alignItems="center">
      <DateTimeRangePicker
        startDate={startDate}
        endDate={endDate}
        today={today}
        onChange={onChange}
        isCustom={isCustom}
        panelRef={panelRef}
      />

      <HStack space={1.5} alignItems="center">
        <StepChevron direction="back" onPress={() => handleStep(-shiftDays)} />

        {presets.map(p => (
          <QuickPickPill
            key={p.value}
            label={t(p.labelKey)}
            active={currentQuickPick === p.value}
            showDot={stepGranularity === p.value && currentQuickPick !== p.value}
            onPress={() => handleQuickPick(p.value)}
          />
        ))}

        <StepChevron direction="forward" disabled={!canStepForward} onPress={() => handleStep(shiftDays)} />
      </HStack>
    </HStack>
  );
};
